import { useState, useMemo } from "react";
import { isWithinInterval, startOfYear, subDays, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isPast, isToday, startOfDay } from "date-fns";
import { DateRange } from "react-day-picker";
import { Transaction } from "@/types/transaction";
import { SortType, FilterType } from "@/components/transactions/TransactionsFilters";
import { fromDateString } from "@/lib/utils/dateUtils";

export type PeriodType = "today" | "thisweek" | "7days" | "30days" | "90days" | "month" | "year" | "all" | "custom";

// Define the possible card filter types
export type CardFilterType = 'vencidos' | 'vencemHoje' | 'aVencer' | 'pagos' | 'total' | null;

interface UseTransactionFiltersOptions {
  activeCardFilter: CardFilterType;
  dateMode?: "payment" | "competence";
}

// Helper function for status sorting priority
function getStatusSortOrder(status: string): string {
  const priority = ['OVERDUE', 'PENDING', 'PAID', 'CANCELLED'];
  const index = priority.indexOf(status);
  return index === -1 ? '999' : index.toString();
}

function filterByPeriod(
  transactions: Transaction[],
  periodType: PeriodType,
  appliedDateRange: DateRange,
  dateMode: "payment" | "competence"
): Transaction[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

  const getDateField = (t: Transaction) => {
    if (dateMode === "competence") {
      return t.competence_date ? fromDateString(t.competence_date) : fromDateString(t.date);
    }
    return fromDateString(t.date);
  };

  if (appliedDateRange.from && appliedDateRange.to) {
    return transactions.filter((t) => {
      const transactionDate = getDateField(t);
      return transactionDate >= appliedDateRange.from! &&
        transactionDate <= appliedDateRange.to!;
    });
  }

  switch (periodType) {
    case "today":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        return transactionDate >= today && transactionDate < tomorrow;
      });
    case "thisweek":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 0 });
        const endOfCurrentWeek = endOfWeek(now, { weekStartsOn: 0 });
        return isWithinInterval(transactionDate, { start: startOfCurrentWeek, end: endOfCurrentWeek });
      });
    case "month":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        const startOfCurrentMonth = startOfMonth(now);
        const endOfCurrentMonth = endOfMonth(now);
        return isWithinInterval(transactionDate, { start: startOfCurrentMonth, end: endOfCurrentMonth });
      });
    case "7days":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        const sevenDaysAgo = subDays(now, 7);
        return transactionDate >= sevenDaysAgo && transactionDate <= now;
      });
    case "30days":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        const thirtyDaysAgo = subDays(now, 30);
        return transactionDate >= thirtyDaysAgo && transactionDate <= now;
      });
    case "90days":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        const ninetyDaysAgo = subMonths(now, 3);
        return transactionDate >= ninetyDaysAgo && transactionDate <= now;
      });
    case "year":
      return transactions.filter((t) => {
        const transactionDate = getDateField(t);
        const startOfCurrentYear = startOfYear(now);
        return transactionDate >= startOfCurrentYear && transactionDate <= now;
      });
    case "all":
    case "custom":
    default:
      return transactions;
  }
}

// Update the hook signature to accept options
export function useTransactionFilters(options?: UseTransactionFiltersOptions) {
  const { activeCardFilter, dateMode = "payment" } = options || {};

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [sortType, setSortType] = useState<SortType>("date-desc");
  const [periodType, setPeriodType] = useState<PeriodType>("all");
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [appliedDateRange, setAppliedDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  // Get count of active recurring transactions
  const getRecurringCount = useMemo(() => {
    return (transactions: Transaction[] | undefined): number => {
      if (!transactions) return 0;
      return transactions.filter(t => t.is_recurring && !t.parent_transaction_id).length;
    };
  }, []);

  const filterTransactions = useMemo(() => {
    return (transactions: Transaction[] | undefined): Transaction[] => {
      if (!transactions) return [];

      let filtered = [...transactions];

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter((transaction) =>
          transaction.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply type/status filter
      switch (filterType) {
        case "income":
          filtered = filtered.filter((t) => t.type === "income");
          break;
        case "expense":
          filtered = filtered.filter((t) => t.type === "expense");
          break;
        case "paid":
          filtered = filtered.filter((t) => t.is_paid);
          break;
        case "pending":
          filtered = filtered.filter((t) => !t.is_paid);
          break;
        case "recurring":
          // Only show parent recurring transactions (not child occurrences)
          filtered = filtered.filter((t) => t.is_recurring && !t.parent_transaction_id);
          break;
        case "all":
        default:
          // No filtering
          break;
      }

      // Apply period filter
      filtered = filterByPeriod(filtered, periodType, appliedDateRange, dateMode);

      // Apply card filter
      const now = new Date();
      const todayStart = startOfDay(now);

      switch (activeCardFilter) {
        case 'vencidos':
          filtered = filtered.filter(t => !t.is_paid && isPast(t.date) && !isToday(t.date));
          break;
        case 'vencemHoje':
          filtered = filtered.filter(t => !t.is_paid && isToday(t.date));
          break;
        case 'aVencer':
          // Filter for pending transactions that are today or in the future
          filtered = filtered.filter(t => !t.is_paid && (isToday(t.date) || t.date > todayStart));
          break;
        case 'pagos':
          filtered = filtered.filter(t => t.is_paid);
          break;
        case 'total':
        case null:
        default:
          // No additional filtering needed for 'total' or null
          break;
      }

      // Apply sorting
      filtered.sort((a, b) => {
        switch (sortType) {
          case "date-desc":
            return fromDateString(b.date).getTime() - fromDateString(a.date).getTime();
          case "date-asc":
            return fromDateString(a.date).getTime() - fromDateString(b.date).getTime();
          case "competence-desc":
            return (b.competence_date ? fromDateString(b.competence_date).getTime() : fromDateString(b.date).getTime()) -
              (a.competence_date ? fromDateString(a.competence_date).getTime() : fromDateString(a.date).getTime());
          case "competence-asc":
            return (a.competence_date ? fromDateString(a.competence_date).getTime() : fromDateString(a.date).getTime()) -
              (b.competence_date ? fromDateString(b.competence_date).getTime() : fromDateString(b.date).getTime());
          case "amount-desc":
            return b.amount - a.amount;
          case "amount-asc":
            return a.amount - b.amount;
          case "description-desc":
            return b.description.localeCompare(a.description, 'pt-BR', { sensitivity: 'base' });
          case "description-asc":
            return a.description.localeCompare(b.description, 'pt-BR', { sensitivity: 'base' });
          case "status-desc":
            return getStatusSortOrder(b.status).localeCompare(getStatusSortOrder(a.status));
          case "status-asc":
            return getStatusSortOrder(a.status).localeCompare(getStatusSortOrder(b.status));
          default:
            return 0;
        }
      });

      return filtered;
    };
  }, [searchTerm, filterType, sortType, periodType, appliedDateRange, activeCardFilter, dateMode]);

  return {
    searchTerm,
    setSearchTerm,
    filterType,
    setFilterType,
    sortType,
    setSortType,
    periodType,
    setPeriodType,
    dateRange,
    setDateRange,
    appliedDateRange,
    setAppliedDateRange,
    filterTransactions,
    getRecurringCount,
  };
}